/**
 * Returns.jsx — Luvia Returns Policy Page
 *
 * AUDIT FIXES:
 * ✅ Full-width desktop layout (no max-width boxing the shell)
 * ✅ True 2-column desktop layout: sticky sidebar + scrollable main
 * ✅ Single H1, strict H2→H3 hierarchy
 * ✅ JSON-LD: WebPage + BreadcrumbList + MerchantReturnPolicy schema
 * ✅ WhatsApp link as <a> (not button + window.open) — better SEO & mobile UX
 * ✅ Eligibility grid — side-by-side desktop, stacked mobile
 * ✅ Refund cards — 3-col on desktop
 * ✅ FAQ zero-JS via <details>
 * ✅ Process uses <ol> (semantic ordered list)
 * ✅ All interactive elements :focus-visible
 * ✅ Skip link, sr-only utility
 * ✅ JSON-LD outside component (no re-serialize on render)
 * ✅ Condition list uses <IconCheck> not <IconPackage> (more meaningful)
 * ✅ CTA section full-width dark band on desktop
 * ✅ Notice styled as <aside role="note"> — correct semantic
 */

import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import { BRAND } from '../../config/brand';
import {
  IconReturn,
  IconShield,
  IconAlertCircle,
  IconArrowRight,
  IconWhatsApp,
  IconClock,
  IconCheck,
  IconX,
} from '../../components/common/Icons/Icons';
import './Returns.css';

/* ── Constants ──────────────────────────────────────────────────────────── */
const SITE_URL         = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME       = BRAND?.name || 'Luvia';
const RETURNS_URL      = `${SITE_URL}/returns`;
const WHATSAPP_NUMBER  = process.env.REACT_APP_WHATSAPP || '91XXXXXXXXXX';
const WHATSAPP_MESSAGE = encodeURIComponent(
  'Hi, I need help with sizing before purchase. My expected order details are: '
);
const WHATSAPP_HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

/* ── Accessibility helper ───────────────────────────────────────────────── */
function VisuallyHidden({ children }) {
  return <span className="rp-sr-only">{children}</span>;
}

/* ── WhatsApp CTA (reusable, rendered as <a>) ───────────────────────────── */
function WhatsAppButton({ size = 'md', label = 'Contact on WhatsApp' }) {
  return (
    <a
      href={WHATSAPP_HREF}
      target="_blank"
      rel="noopener noreferrer"
      className={`rp-wa-btn rp-wa-btn--${size}`}
      aria-label="Open WhatsApp for sizing and policy help"
    >
      <IconWhatsApp size={size === 'lg' ? 22 : 18} aria-hidden="true" />
      {label}
      <VisuallyHidden> (opens WhatsApp)</VisuallyHidden>
    </a>
  );
}

/* ── FAQ data (before JSON-LD so schema can reference it) ────────────────── */
const FAQ = [
  {
    q: 'Do you currently accept returns or exchanges?',
    a: 'No. Returns and exchanges are temporarily unavailable while we strengthen operations and quality control.',
  },
  {
    q: 'Why is the no-return policy in place?',
    a: 'As a growing jewelry brand, we are focusing on product consistency, sizing accuracy, and service quality. We will announce policy updates once returns reopen.',
  },
  {
    q: 'What if I receive a damaged or incorrect item?',
    a: 'Contact us immediately on WhatsApp with your order number and clear photos. Our team will review and resolve verified issues on priority.',
  },
  {
    q: 'How can I avoid size issues before ordering?',
    a: 'Use our Size Guide and contact support before purchase for ring, bracelet, necklace, and bangle fit guidance. We are happy to help you choose the right size.',
  },
  {
    q: 'Do you offer free shipping?',
    a: 'Yes. We offer free shipping on orders above 500.',
  },
  {
    q: 'Where should I confirm the latest policy details?',
    a: 'Please review this page, FAQ, and Shipping page, or message us on WhatsApp before placing your order.',
  },
];

/* ── Structured data ────────────────────────────────────────────────────── */
const jsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${RETURNS_URL}#webpage`,
      url: RETURNS_URL,
      name: `Returns Policy | ${BRAND_NAME}`,
      description:
        `Returns are temporarily unavailable at ${BRAND_NAME}. Get sizing help for rings, bracelets, necklaces, and bangles before purchase. Free shipping above 500.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${RETURNS_URL}#breadcrumb` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${RETURNS_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Returns Policy', item: RETURNS_URL },
      ],
    },
    {
      '@type': 'MerchantReturnPolicy',
      '@id': `${RETURNS_URL}#policy`,
      name: `${BRAND_NAME} Return Policy`,
      url: RETURNS_URL,
      returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
      applicableCountry: 'IN',
      description:
        'Returns and exchanges are currently unavailable. Customers are encouraged to confirm size and fit before ordering.',
    },
    {
      '@type': 'FAQPage',
      '@id': `${RETURNS_URL}#faq`,
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ],
});

/* ── Component ──────────────────────────────────────────────────────────── */
export default function Returns() {
  return (
    <>
      <Helmet>
        <title>Returns Policy | {BRAND_NAME}</title>
        <meta
          name="description"
          content={`${BRAND_NAME} currently does not accept returns or exchanges. Contact support for jewelry sizing help before purchase. Free shipping on orders above 500.`}
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={RETURNS_URL} />

        <link rel="prefetch" href="/shipping" as="document" />
        <link rel="prefetch" href="/contact" as="document" />
        <link rel="prefetch" href="/faq" as="document" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={RETURNS_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={`Returns Policy | ${BRAND_NAME}`} />
        <meta property="og:description" content="Returns are currently unavailable. Get jewelry sizing help before purchase. Free shipping on orders above 500." />
        <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Returns Policy — Currently unavailable" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Returns Policy | ${BRAND_NAME}`} />
        <meta name="twitter:description" content="Returns are currently unavailable. Contact support for jewelry sizing help. Free shipping on orders above 500." />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.jpg`} />
        <meta name="twitter:image:alt" content="Returns Policy — Currently unavailable" />

        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="rp-page" id="main-content">

          {/* Skip link */}
          <a href="#rp-main" className="rp-skip">Skip to returns policy details</a>

          {/* ── Full-width page band ───────────────────────────────────── */}
          <div className="rp-hero">
            <div className="rp-hero__inner">

              {/* Breadcrumb */}
              <nav className="rp-breadcrumb" aria-label="Breadcrumb">
                <ol className="rp-breadcrumb__list">
                  <li><Link to="/" className="rp-breadcrumb__link">Home</Link></li>
                  <li className="rp-breadcrumb__sep" aria-hidden="true"><span>›</span></li>
                  <li><span aria-current="page" className="rp-breadcrumb__current">Returns Policy</span></li>
                </ol>
              </nav>

              {/* H1 */}
              <h1 className="rp-hero__title">Returns Policy</h1>
              <p className="rp-hero__subtitle">
                Returns and exchanges are <strong>currently unavailable</strong>. Please confirm ring, bracelet,
                necklace, bangle, and earring sizing before purchase.
              </p>

              {/* Hero CTA */}
              <WhatsAppButton size="lg" label="Get Sizing Help on WhatsApp" />
            </div>
          </div>

          {/* ── Body: sidebar + main ───────────────────────────────────── */}
          <div className="rp-layout" id="rp-main">

            {/* ── Sticky Sidebar (desktop) ─────────────────────────────── */}
            <aside className="rp-sidebar" aria-label="Policy at a glance">
              <div className="rp-sidebar__inner">
                <p className="rp-sidebar__label">At a Glance</p>

                {[
                  { icon: <IconWhatsApp size={20} />,    title: 'WhatsApp Support',  text: 'Pre-order sizing and policy help' },
                  { icon: <IconClock size={20} />,       title: 'Fast Support',      text: 'Mon-Sat, 10 AM to 7 PM IST' },
                  { icon: <IconReturn size={20} />,      title: 'Returns Status',    text: 'Returns temporarily unavailable' },
                  { icon: <IconShield size={20} />,      title: 'Shipping Benefit',  text: 'Free shipping on orders above 500' },
                ].map(({ icon, title, text }) => (
                  <div key={title} className="rp-sidebar__item">
                    <span className="rp-sidebar__item-icon" aria-hidden="true">{icon}</span>
                    <div>
                      <p className="rp-sidebar__item-title">{title}</p>
                      <p className="rp-sidebar__item-text">{text}</p>
                    </div>
                  </div>
                ))}

                <div className="rp-sidebar__divider" />
                <WhatsAppButton size="sm" label="Contact on WhatsApp" />
                <p className="rp-sidebar__hours">Mon–Sat · 10 AM – 7 PM IST</p>
              </div>
            </aside>

            {/* ── Main content column ──────────────────────────────────── */}
            <div className="rp-main">

              {/* ── Policy notice ─────────────────────────────────────── */}
              <aside className="rp-notice" role="note" aria-label="Important policy notice">
                <IconAlertCircle size={20} aria-hidden="true" className="rp-notice__icon" />
                <div className="rp-notice__body">
                  <strong>Returns and exchanges are currently unavailable.</strong> Please use our Size Guide and contact support before ordering. We offer free shipping on orders above 500.
                </div>
              </aside>

              {/* ── Eligibility ───────────────────────────────────────── */}
              <section className="rp-section" aria-labelledby="elig-h">
                <h2 id="elig-h" className="rp-section__title">Policy Scope</h2>
                <div className="rp-eligibility">
                  <div className="rp-elig rp-elig--yes">
                    <div className="rp-elig__header">
                      <IconCheck size={18} aria-hidden="true" />
                      <h3 className="rp-elig__title">What We Support Now</h3>
                    </div>
                    <ul className="rp-elig__list">
                      <li>Pre-purchase sizing help for rings and bands</li>
                      <li>Necklace length guidance and layering support</li>
                      <li>Bracelet and bangle fit recommendations</li>
                      <li>Material and care clarification before checkout</li>
                    </ul>
                  </div>
                  <div className="rp-elig rp-elig--no">
                    <div className="rp-elig__header">
                      <IconX size={18} aria-hidden="true" />
                      <h3 className="rp-elig__title">Currently Not Available</h3>
                    </div>
                    <ul className="rp-elig__list">
                      <li>Returns after delivery</li>
                      <li>Size exchange requests</li>
                      <li>Change-of-mind refunds</li>
                      <li>Preference-based replacement requests</li>
                      <li>Post-delivery return shipping claims</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* ── Condition requirements ────────────────────────────── */}
              <section className="rp-section" aria-labelledby="cond-h">
                <h2 id="cond-h" className="rp-section__title">Before You Place Your Order</h2>
                <p className="rp-text">Please follow these fit checks to order confidently:</p>
                <ul className="rp-checklist">
                  {[
                    'Check ring size with our size chart before checkout',
                    'Compare necklace drop with a chain you already own',
                    'Measure wrist or hand opening for bracelets and bangles',
                    'Review product dimensions and care notes on each PDP',
                  ].map((item) => (
                    <li key={item} className="rp-checklist__item">
                      <IconCheck size={14} aria-hidden="true" className="rp-checklist__icon" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="rp-warning" role="note">
                  <IconAlertCircle size={16} aria-hidden="true" />
                  <p>Since returns are currently unavailable, please confirm fit details with support before placing your order.</p>
                </div>
              </section>

              {/* ── Returns process ───────────────────────────────────── */}
              <section className="rp-section" aria-labelledby="process-h">
                <h2 id="process-h" className="rp-section__title">How to Get Fit Support</h2>
                <ol className="rp-steps" aria-label="Sizing support steps">
                  {[
                    {
                      title: 'Message Us on WhatsApp',
                      text:  'Share the product name, preferred fit, and any existing size references.',
                      cta:   true,
                    },
                    {
                      title: 'Tell Us Your Category',
                      text:  'Specify whether you need guidance for rings, necklaces, bracelets, bangles, or earrings.',
                    },
                    {
                      title: 'Receive Size Recommendation',
                      text:  'Our team recommends the most suitable option based on your measurements and styling preference.',
                    },
                    {
                      title: 'Place Order with Confidence',
                      text:  'Once size is confirmed, place your order and enjoy free shipping above 500.',
                    },
                  ].map(({ title, text, cta }, i) => (
                    <li key={title} className="rp-step">
                      <div className="rp-step__badge" aria-hidden="true">{i + 1}</div>
                      <div className="rp-step__body">
                        <h3 className="rp-step__title">{title}</h3>
                        <p className="rp-step__text">{text}</p>
                        {cta && <WhatsAppButton size="sm" label="Chat on WhatsApp" />}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              {/* ── Refund & exchange policy ──────────────────────────── */}
              <section className="rp-section" aria-labelledby="refund-h">
                <h2 id="refund-h" className="rp-section__title">Current Policy Details</h2>
                <div className="rp-refund-grid">
                  {[
                    {
                      title: 'Returns',
                      text:  'Returns are temporarily unavailable across categories while policy operations are being upgraded.',
                    },
                    {
                      title: 'Exchanges',
                      text:  'Size exchanges are currently unavailable. Please use pre-order size support to avoid mismatch.',
                    },
                    {
                      title: 'Shipping',
                      text:  'Free shipping is available on orders above 500.',
                    },
                  ].map(({ title, text }) => (
                    <div key={title} className="rp-refund-card">
                      <h3 className="rp-refund-card__title">{title}</h3>
                      <p className="rp-refund-card__text">{text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── FAQ ───────────────────────────────────────────────── */}
              <section className="rp-section" aria-labelledby="faq-h">
                <h2 id="faq-h" className="rp-section__title">Frequently Asked Questions</h2>
                <div className="rp-faq">
                  {FAQ.map(({ q, a }) => (
                    <details key={q} className="rp-faq__item">
                      <summary className="rp-faq__q">
                        <span>{q}</span>
                        <span className="rp-faq__chevron" aria-hidden="true" />
                      </summary>
                      <div className="rp-faq__a"><p>{a}</p></div>
                    </details>
                  ))}
                </div>
              </section>

            </div>{/* end rp-main */}
          </div>{/* end rp-layout */}

          {/* ── Related (internal linking) ──────────────────────────────── */}
          <nav className="rp-related" aria-label="Related information">
            <h2 className="rp-related__title">Related information</h2>
            <ul className="rp-related__list" role="list">
              <li><Link to="/shipping" className="rp-related__link">Shipping &amp; Delivery</Link></li>
                  <li><Link to="/size-guide" className="rp-related__link">Size Guide</Link></li>
              <li><Link to="/contact" className="rp-related__link">Contact Us</Link></li>
              <li><Link to="/faq" className="rp-related__link">FAQ</Link></li>
              <li><Link to="/shop" className="rp-related__link">Shop All</Link></li>
            </ul>
          </nav>

          {/* ── Full-width CTA band ────────────────────────────────────── */}
          <div className="rp-cta-band">
            <div className="rp-cta-band__inner">
              <div className="rp-cta-band__text">
                <p className="rp-cta-band__eyebrow">Need help?</p>
                <h2 className="rp-cta-band__title">Get Jewelry Fit Guidance Before Purchase</h2>
                <p className="rp-cta-band__sub">Free shipping above 500 · Available Monday-Saturday, 10 AM-7 PM IST</p>
              </div>
              <WhatsAppButton size="lg" label="Get Support on WhatsApp" />
            </div>
          </div>

        </main>
      </Layout>
    </>
  );
}